import { useEffect, useMemo, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Plus, ShieldCheck, Trash2, Upload, UserPlus } from 'lucide-react'
import { generateUUID } from '@/lib/utils'
import type { Contact } from '@/routes/_public/app'
import { useContactPicker } from '@/hooks/use-contacts'
import {
  detectDeviceFromUserAgent,
  getHelpPath,
  type DeviceKey,
} from '@/lib/help-center'
import { Badge } from '@/components/ui/badge'

interface ContactImportProps {
  onComplete: (contacts: Contact[]) => void
}

interface FormContact {
  id: string
  name: string
  phone: string
  email: string
}

export function ContactImport({ onComplete }: ContactImportProps) {
  const { isContactPickerSupported } = useContactPicker()
  const csvInputRef = useRef<HTMLInputElement | null>(null)
  const debugEnabled = useMemo(() => {
    if (typeof window === 'undefined') return false
    const params = new URLSearchParams(window.location.search)
    return params.get('debug') === 'true'
  }, [])
  const helpLink = useMemo(() => {
    const device = detectDeviceFromUserAgent()
    const map: Record<DeviceKey, string> = {
      all: '/app/help',
      iphone: getHelpPath('import-iphone'),
      ipad: getHelpPath('import-ipad'),
      android: getHelpPath('import-android'),
      windows: getHelpPath('windows-phone-link'),
      macos: '/app/help',
      linux: '/app/help',
    }
    return map[device] ?? '/app/help'
  }, [])
  const showIphoneHelp = useMemo(() => {
    if (typeof navigator === 'undefined') return false
    const ua = navigator.userAgent
    const isIphoneOrIpad = /iPhone|iPad/i.test(ua)
    const isSafari = /Safari/i.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/i.test(ua)
    return isIphoneOrIpad && isSafari
  }, [])
  const [debugInfo, setDebugInfo] = useState<Record<string, string>>({})

  useEffect(() => {
    if (!debugEnabled) return
    const hasNavigator = typeof navigator !== 'undefined'
    const contactsExists = hasNavigator && 'contacts' in navigator
    const contactsValue = contactsExists ? String((navigator as any).contacts) : 'undefined'
    const selectExists =
      contactsExists &&
      (navigator as any).contacts &&
      'select' in (navigator as any).contacts
    setDebugInfo({
      'protocol': typeof window !== 'undefined' ? window.location.protocol : 'unknown',
      'host': typeof window !== 'undefined' ? window.location.host : 'unknown',
      'userAgent': hasNavigator ? navigator.userAgent : 'unknown',
      'navigator.contacts': contactsExists ? contactsValue : 'missing',
      'navigator.contacts.select': selectExists ? 'present' : 'missing',
      'isContactPickerSupported': String(isContactPickerSupported),
    })
  }, [debugEnabled, isContactPickerSupported])
  const [formContacts, setFormContacts] = useState<FormContact[]>([
    { id: generateUUID(), name: '', phone: '', email: '' },
  ])
  const [csvError, setCsvError] = useState('')
  const [isDragging, setIsDragging] = useState(false)

  const handleImportFromPhone = async () => {
    try {
      // Define the properties you want to access
      const properties: ('name' | 'email' | 'tel')[] = ['name', 'email', 'tel'];
      const options = { multiple: true };

      // Open the contact picker
      const selectedContacts = await (navigator.contacts as ContactsManager).select(properties, options);

      if (selectedContacts.length === 0) {
        // User closed the picker or didn't select anyone
        return;
      }

      // Transform the data to match our application's Contact type
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const formattedContacts: Contact[] = selectedContacts.map((contact: any) => ({
        id: generateUUID(), // Use our existing UUID generator
        name: contact.name?.[0] || '',
        email: contact.email?.[0] || undefined,
        phone: contact.tel?.[0] || undefined,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      })).filter((c: any) => c.name && (c.email || c.phone)); // Ensure contacts are valid

      if (formattedContacts.length > 0) {
        onComplete(formattedContacts);
      }

    } catch (ex) {
      // The user may have denied permission or an error occurred.
      console.error('Error fetching contacts:', ex);
      alert('Could not get contacts. Please ensure you have granted permission.');
    }
  };

  const addRow = () => {
    setFormContacts([
      ...formContacts,
      { id: generateUUID(), name: '', phone: '', email: '' },
    ])
  }

  const removeRow = (id: string) => {
    if (formContacts.length > 1) {
      setFormContacts(formContacts.filter((c) => c.id !== id))
    }
  }

  const updateContact = (
    id: string,
    field: keyof FormContact,
    value: string,
  ) => {
    setFormContacts(
      formContacts.map((c) => (c.id === id ? { ...c, [field]: value } : c)),
    )
  }

  const handleManualSubmit = () => {
    const validContacts = formContacts
      .filter((c) => c.name.trim() && (c.phone.trim() || c.email.trim()))
      .map((c) => ({
        id: c.id,
        name: c.name.trim(),
        phone: c.phone.trim() || undefined,
        email: c.email.trim() || undefined,
      }))

    if (validContacts.length === 0) {
      alert('Please add at least one contact with a name and phone or email.')
      return
    }

    onComplete(validContacts)
  }

  const handleCsvText = (text: string) => {
    setCsvError('')
    try {
      const { contacts, issues } = parseCSV(text)

      if (issues.length > 0) {
        setCsvError(formatCsvIssues(issues))
        return
      }

      if (contacts.length === 0) {
        setCsvError('No valid contacts found in CSV data.')
        return
      }

      onComplete(contacts)
    } catch (error) {
      setCsvError(
        error instanceof Error ? error.message : 'Failed to parse CSV data.',
      )
    }
  }

  const handleCSVUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setCsvError('')
    const reader = new FileReader()

    reader.onload = (event) => {
      const text = event.target?.result as string
      handleCsvText(text)
    }

    reader.readAsText(file)
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.stopPropagation()
    setIsDragging(false)
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    const file = e.dataTransfer.files?.[0]
    if (file && isCsvLikeFile(file)) {
      // Simulate a ChangeEvent to reuse handleCSVUpload
      const dataTransfer = new DataTransfer()
      dataTransfer.items.add(file)
      const syntheticEvent = {
        target: { files: dataTransfer.files },
      } as React.ChangeEvent<HTMLInputElement>
      handleCSVUpload(syntheticEvent)
      return
    }

    const text = e.dataTransfer.getData('text')
    if (text && text.trim()) {
      handleCsvText(text)
      return
    } else {
      setCsvError('Only CSV/TSV files or pasted data are supported.')
    }
  }

  const handlePasteCsv = (e: React.ClipboardEvent<HTMLDivElement>) => {
    const text = e.clipboardData.getData('text')
    if (!text || !text.trim()) {
      setCsvError('Clipboard was empty.')
      return
    }
    e.preventDefault()
    handleCsvText(text)
  }

  const handleCsvBoxClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement
    if (target.closest('a,button,input,select,textarea,label')) return
    csvInputRef.current?.click()
  }

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h1 className="text-3xl font-bold">Import Contacts</h1>
        <p className="text-slate-600">
          Add your contacts manually or upload a CSV file.
        </p>
        <p className="text-sm">
          <a
            href={helpLink}
            className="text-slate-600 underline hover:text-slate-900"
          >
            Need help importing? Open the guide.
          </a>
        </p>
      </div>

      {(showIphoneHelp || debugEnabled) && (
        <div className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-700">
          <div className="font-semibold text-slate-900">How to Import from iPhone</div>
          <div className="mt-2 space-y-2">
            <p>
              Safari on iOS hides the Contact Picker behind an experimental flag. To enable it:
            </p>
            <ol className="list-decimal pl-5 space-y-1">
              <li>Open Settings on your iPhone.</li>
              <li>Go to Safari → Advanced → Feature Flags.</li>
              <li>Enable “Contact Picker API”.</li>
              <li>Reload this page in Safari and tap “Import from Phone”.</li>
            </ol>
            <p className="text-xs text-slate-500">
              Note: This requires HTTPS and only works in Safari on iOS (not in desktop mode).
            </p>
          </div>
        </div>
      )}

      <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-5 text-sm text-emerald-900">
        <div className="flex items-center gap-2 font-semibold text-emerald-900">
          <Badge className="bg-emerald-600 text-white">
            <ShieldCheck className="h-3.5 w-3.5 mr-1" />
            Privacy &amp; Security
          </Badge>
          Your data stays in your browser
        </div>
        <p className="mt-2 text-emerald-800">
          Imports and drafts are handled locally in your browser. We do not store
          your contacts or message content.
        </p>
        <p className="mt-2 text-emerald-800">
          You can even disconnect from the internet after the app loads and it
          will continue to work. Read the{' '}
          <a
            href={getHelpPath('privacy-security')}
            className="underline hover:text-emerald-900"
          >
            Privacy &amp; Security guide
          </a>{' '}
          for details.
        </p>
      </div>

      {debugEnabled && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <div className="font-semibold mb-2">Debug Info</div>
          <div className="space-y-1">
            {Object.entries(debugInfo).map(([key, value]) => (
              <div key={key} className="break-words">
                <span className="font-medium">{key}:</span> {value}
              </div>
            ))}
          </div>
        </div>
      )}

      <Tabs defaultValue="manual" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="manual">Manual Entry</TabsTrigger>
          <TabsTrigger value="csv">CSV Upload</TabsTrigger>
        </TabsList>

        {isContactPickerSupported && (
           <Button variant="outline" onClick={handleImportFromPhone} className="w-full mt-2">
             <UserPlus className="h-4 w-4 mr-2" />
             Import from Phone
           </Button>
        )}

        <TabsContent value="manual" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Add Contacts</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {formContacts.map((contact) => (
                <div
                  key={contact.id}
                  className="grid grid-cols-1 md:grid-cols-12 gap-3 p-4 border rounded-lg"
                >
                  <div className="md:col-span-4">
                    <Label htmlFor={`name-${contact.id}`}>
                      Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id={`name-${contact.id}`}
                      placeholder="John Doe"
                      value={contact.name}
                      onChange={(e) =>
                        updateContact(contact.id, 'name', e.target.value)
                      }
                    />
                  </div>
                  <div className="md:col-span-3">
                    <Label htmlFor={`phone-${contact.id}`}>Phone</Label>
                    <Input
                      id={`phone-${contact.id}`}
                      type="tel"
                      placeholder="555-201-1234"
                      value={contact.phone}
                      onChange={(e) =>
                        updateContact(contact.id, 'phone', e.target.value)
                      }
                    />
                  </div>
                  <div className="md:col-span-4">
                    <Label htmlFor={`email-${contact.id}`}>Email</Label>
                    <Input
                      id={`email-${contact.id}`}
                      type="email"
                      placeholder="john@example.com"
                      value={contact.email}
                      onChange={(e) =>
                        updateContact(contact.id, 'email', e.target.value)
                      }
                    />
                  </div>
                  <div className="md:col-span-1 flex items-end">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeRow(contact.id)}
                      disabled={formContacts.length === 1}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}

              <Button variant="outline" onClick={addRow} className="w-full">
                <Plus className="h-4 w-4 mr-2" />
                Add Another Contact
              </Button>

              <p className="text-sm text-slate-600">
                <span className="text-red-500">*</span> Name and at least one
                contact method (phone or email) required.
              </p>

              <Button onClick={handleManualSubmit} className="w-full" size="lg">
                Continue with {formContacts.filter((c) => c.name.trim()).length}{' '}
                Contact(s)
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="csv" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Upload CSV File</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center space-y-4 ${
                  isDragging ? 'border-blue-500 bg-blue-50' : 'border-slate-200'
                }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onPaste={handlePasteCsv}
                onClick={handleCsvBoxClick}
                tabIndex={0}
                role="button"
                aria-label="Paste CSV data or upload CSV file"
              >
                <Upload className="h-12 w-12 mx-auto text-slate-400" />
                <div>
                  <Label
                    htmlFor="csv-upload"
                    className="cursor-pointer text-slate-900 hover:underline"
                  >
                    Click to upload, drag and drop, or paste CSV/TSV data
                  </Label>
                  <Input
                    id="csv-upload"
                    type="file"
                    accept=".csv,.tsv,text/csv,text/tab-separated-values"
                    className="hidden"
                    onChange={handleCSVUpload}
                    ref={csvInputRef}
                  />
                </div>
                <p className="text-sm text-slate-600">
                  CSV or TSV must have a header row with Name, Phone, and/or Email
                  columns. You can also click here and paste CSV/TSV data.
                </p>
              </div>

              {csvError && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm whitespace-pre-line">
                  {csvError}
                </div>
              )}

              <div className="bg-slate-50 p-4 rounded-lg space-y-2">
                <p className="font-semibold text-sm">CSV Format Example:</p>
                <pre className="text-xs bg-white p-3 rounded border overflow-x-auto">
                  {`Name,Phone,Email
John Doe,555-208-1234,john@example.com
Jane Smith,555-408-5678,
Bob Johnson,,bob@example.com`}
                </pre>
                <p className="text-xs text-slate-600">
                  The first row must be a header with 'Name', 'Phone', and/or 'Email' columns. The order of columns does not matter.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}

type CsvParseResult = {
  contacts: Contact[]
  issues: string[]
}

type HeaderField = 'name' | 'phone' | 'email' | 'first' | 'last'

const HEADER_SYNONYMS: Record<HeaderField, string[]> = {
  name: ['name', 'full name', 'fullname', 'display name', 'nick name', 'nickname'],
  phone: [
    'phone',
    'phone number',
    'cell phone',
    'cellphone',
    'mobile',
    'mobile phone',
    'cell',
    'telephone',
    'tel',
  ],
  email: ['email', 'e-mail', 'email address'],
  first: ['first', 'first name', 'given name'],
  last: ['last', 'last name', 'surname', 'family name'],
}

function normalizeHeader(value: string) {
  return value.trim().toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()
}

function resolveHeaderField(headerValue: string): HeaderField | null {
  const normalized = normalizeHeader(headerValue)
  for (const [field, synonyms] of Object.entries(HEADER_SYNONYMS)) {
    if (synonyms.includes(normalized)) {
      return field as HeaderField
    }
  }
  return null
}

function parseCsvRows(text: string): string[][] {
  const delimiter = detectDelimiter(text)
  const rows: string[][] = []
  let currentRow: string[] = []
  let currentField = ''
  let inQuotes = false

  for (let i = 0; i < text.length; i++) {
    const char = text[i]

    if (char === '"') {
      if (inQuotes && text[i + 1] === '"') {
        currentField += '"'
        i++
      } else {
        inQuotes = !inQuotes
      }
      continue
    }

    if (char === delimiter && !inQuotes) {
      currentRow.push(currentField.trim())
      currentField = ''
      continue
    }

    if ((char === '\n' || char === '\r') && !inQuotes) {
      if (char === '\r' && text[i + 1] === '\n') {
        i++
      }
      currentRow.push(currentField.trim())
      const hasValues = currentRow.some((field) => field.trim() !== '')
      if (hasValues) {
        rows.push(currentRow)
      }
      currentRow = []
      currentField = ''
      continue
    }

    currentField += char
  }

  if (currentField.length > 0 || currentRow.length > 0) {
    currentRow.push(currentField.trim())
    const hasValues = currentRow.some((field) => field.trim() !== '')
    if (hasValues) {
      rows.push(currentRow)
    }
  }

  return rows
}

function detectDelimiter(text: string) {
  const sample = text.slice(0, 2048)
  const hasTabs = sample.includes('\t')
  const hasCommas = sample.includes(',')
  if (hasTabs && !hasCommas) return '\t'
  return ','
}

function isCsvLikeFile(file: File) {
  const name = file.name.toLowerCase()
  if (name.endsWith('.csv') || name.endsWith('.tsv')) return true
  if (file.type === 'text/csv') return true
  if (file.type === 'text/tab-separated-values') return true
  return false
}

function normalizeName(value: string) {
  const trimmed = value.trim()
  if (!trimmed.includes(',')) return trimmed
  const [last, first, ...rest] = trimmed.split(',').map((part) => part.trim())
  if (!first) return trimmed
  const remaining = rest.length > 0 ? ` ${rest.join(' ')}` : ''
  return `${first} ${last}${remaining}`.trim()
}

function formatCsvIssues(issues: string[]) {
  return ['We found issues in your CSV file:', ...issues.map((issue) => `- ${issue}`)].join(
    '\n',
  )
}

function parseCSV(text: string): CsvParseResult {
  const rows = parseCsvRows(text)

  if (rows.length < 2) {
    throw new Error(
      'CSV file must contain at least a header row and one data row.',
    )
  }

  const headerRow = rows[0]
  const resolvedHeaders = headerRow.map(resolveHeaderField)

  const nameIndex = resolvedHeaders.indexOf('name')
  const phoneIndex = resolvedHeaders.indexOf('phone')
  const emailIndex = resolvedHeaders.indexOf('email')
  const firstIndex = resolvedHeaders.indexOf('first')
  const lastIndex = resolvedHeaders.indexOf('last')

  if (nameIndex === -1 && (firstIndex === -1 || lastIndex === -1)) {
    return {
      contacts: [],
      issues: ['Missing a "Name", "Full Name", "Nick Name", or "First Name" + "Last Name" header.'],
    }
  }
  if (phoneIndex === -1 && emailIndex === -1) {
    return {
      contacts: [],
      issues: ['Missing a "Phone" (e.g. "Cell Phone" or "Mobile") or "Email" header.'],
    }
  }

  const contacts: Contact[] = []
  const issues: string[] = []
  const skippedRows: number[] = []
  const missingNameRows: number[] = []
  const missingContactRows: number[] = []

  for (let i = 1; i < rows.length; i++) {
    const fields = rows[i]
    if (fields.length === 0 || fields.every((field) => !field.trim())) continue

    const rawName =
      nameIndex !== -1 ? (fields[nameIndex] ?? '') : ''
    const firstName =
      firstIndex !== -1 ? (fields[firstIndex] ?? '') : ''
    const lastName =
      lastIndex !== -1 ? (fields[lastIndex] ?? '') : ''

    const combinedName =
      rawName || [firstName, lastName].filter(Boolean).join(' ').trim()
    const name = normalizeName(combinedName)

    const phone = phoneIndex !== -1 ? (fields[phoneIndex] ?? '') : ''
    const email = emailIndex !== -1 ? (fields[emailIndex] ?? '') : ''

    if (!name) {
      missingNameRows.push(i + 1)
      skippedRows.push(i + 1)
      continue
    }
    if (!phone && !email) {
      missingContactRows.push(i + 1)
      skippedRows.push(i + 1)
      continue
    }

    contacts.push({
      id: generateUUID(),
      name,
      phone: phone || undefined,
      email: email || undefined,
    })
  }

  if (missingNameRows.length > 0) {
    issues.push(
      `Missing name values on row(s): ${missingNameRows.slice(0, 5).join(', ')}${
        missingNameRows.length > 5 ? '...' : ''
      }.`,
    )
  }
  if (missingContactRows.length > 0) {
    issues.push(
      `Missing phone/email values on row(s): ${missingContactRows
        .slice(0, 5)
        .join(', ')}${missingContactRows.length > 5 ? '...' : ''}.`,
    )
  }
  if (skippedRows.length > 0) {
    issues.push(
      `We skipped ${skippedRows.length} row(s) because required data was missing.`,
    )
  }

  return { contacts, issues }
}
